
import CompanyBranding from '@src/components/CompanyBranding';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@src/components/ui/accordion.jsx';
import { useNavigateToTop } from '@src/hooks/useNavigateToTop';

// Removed the interface definition for PrivacyPolicyPageProps

export function PrivacyPolicyPage() {

  const onNavigateToTop = useNavigateToTop();

  return (
    <div className="min-h-screen bg-white">
      {/* Header Section */}
      <div className="py-16 border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* SecureOneLabs Logo */}
          <CompanyBranding onlyLogo={true} />

          <div className="mb-8">
            <h1 style={{ color: '#1e638d' }} className="text-3xl font-bold">
              SecureOneLabs
            </h1>
          </div>

          {/* Page Title */}
          <h1 className="text-4xl font-bold text-gray-900 mb-6">
            Privacy Policy
          </h1>

          {/* Last Updated */}
          {/* Note: className was defined twice, fixed to use the tailwind class instead of the style color */}
          <p className="text-sm text-[#999999] mb-6">
            Last updated: March 19, 2026
          </p>

          {/* Intro Paragraph */}
          <p style={{ fontSize: '16px', lineHeight: '20px' }} className="text-base leading-relaxed text-gray-700 max-w-2xl mx-auto">
            SecureOneLabs, the company behind BOSSS, is committed to protecting your privacy.
            This page explains how we handle your data.
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <Accordion collapsible type="single" className="space-y-4">

              {/* Section 1: Information We Collect */}
              <AccordionItem value="information-collect" className="border border-gray-200 rounded-lg">
                <AccordionTrigger className="px-6 py-4 hover:no-underline">
                  <h2 style={{ color: '#1e638d' }} className="text-lg font-bold text-left">
                    1. Information We Collect
                  </h2>
                </AccordionTrigger>

                <AccordionContent className="px-6 pb-6">
                  <div className="space-y-4" style={{ fontSize: '16px', lineHeight: '20px' }}>
                    <p className="text-gray-700">
                      We collect information you provide directly to us, such as when you create an account,
                      use our services, or contact us for support.
                    </p>

                    <div className="space-y-2">
                      <h4 className="font-medium text-gray-900">Types of information we collect:</h4>

                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Account information (name, email, company details)</li>
                        <li>Usage data and analytics</li>
                        <li>Communication preferences</li>
                        <li>Technical information (IP address, browser type)</li>
                      </ul>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Section 2: How We Use Information */}
              <AccordionItem value="information-use" className="border border-gray-200 rounded-lg">
                <AccordionTrigger className="px-6 py-4 hover:no-underline">
                  <h2 style={{ color: '#1e638d' }} className="text-lg font-bold text-left">
                    2. How We Use Information
                  </h2>
                </AccordionTrigger>

                <AccordionContent className="px-6 pb-6">
                  <div className="space-y-4" style={{ fontSize: '16px', lineHeight: '20px' }}>
                    <p className="text-gray-700">
                      We use the information we collect to provide, maintain, and improve our services.
                    </p>

                    <div className="space-y-2">
                      <h4 className="font-medium text-gray-900">We use your information to:</h4>

                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Provide and operate the BOSSS platform</li>
                        <li>Send you technical notices and support messages</li>
                        <li>Respond to your comments and questions</li>
                        <li>Improve our services and develop new features</li>
                        <li>Ensure security and prevent fraud</li>
                      </ul>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Section 3: How We Share Information */}
              <AccordionItem value="information-share" className="border border-gray-200 rounded-lg">
                <AccordionTrigger className="px-6 py-4 hover:no-underline">
                  <h2 style={{ color: '#1e638d' }} className="text-lg font-bold text-left">
                    3. How We Share Information
                  </h2>
                </AccordionTrigger>

                <AccordionContent className="px-6 pb-6">
                  <div className="space-y-4" style={{ fontSize: '16px', lineHeight: '20px' }}>
                    <p className="text-gray-700">
                      We do not sell, trade, or otherwise transfer your personal information to third parties
                      without your consent, except as described in this policy.
                    </p>

                    <div className="space-y-2">
                      <h4 className="font-medium text-gray-900">We may share information:</h4>

                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>With your consent or at your direction</li>
                        <li>With service providers who assist us in operating our platform</li>
                        <li>To comply with legal obligations</li>
                        <li>To protect our rights and prevent fraud</li>
                      </ul>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Section 4: Data Security */}
              <AccordionItem value="data-security" className="border border-gray-200 rounded-lg">
                <AccordionTrigger className="px-6 py-4 hover:no-underline">
                  <h2 style={{ color: '#1e638d' }} className="text-lg font-bold text-left">
                    4. Data Security
                  </h2>
                </AccordionTrigger>

                <AccordionContent className="px-6 pb-6">
                  <div className="space-y-4" style={{ fontSize: '16px', lineHeight: '20px' }}>
                    <p className="text-gray-700">
                      We implement appropriate technical and organizational measures to protect your
                      personal information against unauthorized access, alteration, disclosure, or destruction.
                    </p>

                    <div className="space-y-2">
                      <h4 className="font-medium text-gray-900">Security measures include:</h4>

                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Encryption of data in transit and at rest</li>
                        <li>Regular security assessments and updates</li>
                        <li>Access controls and authentication</li>
                        <li>Employee training on data protection</li>
                      </ul>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Section 5: Your Rights */}
              <AccordionItem value="your-rights" className="border border-gray-200 rounded-lg">
                <AccordionTrigger className="px-6 py-4 hover:no-underline">
                  <h2 style={{ color: '#1e638d' }} className="text-lg font-bold text-left">
                    5. Your Rights
                  </h2>
                </AccordionTrigger>

                <AccordionContent className="px-6 pb-6">
                  <div className="space-y-4" style={{ fontSize: '16px', lineHeight: '20px' }}>
                    <p className="text-gray-700">
                      You have certain rights regarding your personal information, subject to applicable laws.
                    </p>

                    <div className="space-y-2">
                      <h4 className="font-medium text-gray-900">Your rights include:</h4>

                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Access to your personal information</li>
                        <li>Correction of inaccurate information</li>
                        <li>Deletion of your personal information</li>
                        <li>Portability of your data</li>
                        <li>Objection to processing in certain circumstances</li>
                      </ul>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Section 6: Cookies and Tracking */}
              <AccordionItem value="cookies-tracking" className="border border-gray-200 rounded-lg">
                <AccordionTrigger className="px-6 py-4 hover:no-underline">
                  <h2 style={{ color: '#1e638d' }} className="text-lg font-bold text-left">
                    6. Cookies and Tracking
                  </h2>
                </AccordionTrigger>

                <AccordionContent className="px-6 pb-6">
                  <div className="space-y-4" style={{ fontSize: '16px', lineHeight: '20px' }}>
                    <p className="text-gray-700">
                      Strictly necessary cookies run on every visit — they handle your session,
                      protect forms against abuse, and remember your cookie choice. Everything else
                      runs only if you agree to it first.
                    </p>

                    <div className="space-y-2">
                      <h4 className="font-medium text-gray-900">Always active (no consent required):</h4>

                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Session handling and authentication</li>
                        <li>Security tokens and spam prevention</li>
                        <li>Remembering your cookie preferences</li>
                      </ul>
                    </div>

                    <div className="space-y-2">
                      <h4 className="font-medium text-gray-900">Only with your consent:</h4>

                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Analytics — Sentry session replay, for diagnosing errors</li>

                        <li>
                          Marketing — Microsoft Clarity session recording and heatmaps, which share
                          an advertising identifier with Bing
                        </li>
                      </ul>
                    </div>

                    <p className="text-gray-700">
                      We ask before any of these load, refusing is as easy as accepting, and you can
                      change or withdraw your choice at any time using the
                      {' '}
                      <strong>Cookie Preferences</strong>
                      {' '}
                      link in the footer. For the full list of cookies, what each one does and how
                      long it lasts, see our
                      {' '}

                      <button
                        type="button"
                        className="underline"
                        style={{ color: '#1e638d' }}
                        onClick={() => onNavigateToTop('/cookie-policy')}
                      >
                        Cookie Policy
                      </button>
                      .
                    </p>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Section 7: Changes to This Policy */}
              <AccordionItem value="policy-changes" className="border border-gray-200 rounded-lg">
                <AccordionTrigger className="px-6 py-4 hover:no-underline">
                  <h2 style={{ color: '#1e638d' }} className="text-lg font-bold text-left">
                    7. Changes to This Policy
                  </h2>
                </AccordionTrigger>

                <AccordionContent className="px-6 pb-6">
                  <div className="space-y-4" style={{ fontSize: '16px', lineHeight: '20px' }}>
                    <p className="text-gray-700">
                      We may update this Privacy Policy from time to time. We will notify you of any
                      material changes by posting the new policy on this page and updating the &quot;Last updated&quot; date.
                    </p>

                    <p className="text-gray-700">
                      Your continued use of our services after any changes indicates your acceptance of the updated policy.
                    </p>
                  </div>
                </AccordionContent>
              </AccordionItem>

            </Accordion>
          </div>
        </div>
      </div>

      {/* Contact Block */}
      <div className="border-t border-gray-200 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-gray-50 rounded-lg p-8">
            <h3 style={{ fontSize: '16px' }} className="font-bold text-gray-900 mb-4">
              Questions?
            </h3>

            <p className="text-gray-700 mb-4" style={{ fontSize: '16px', lineHeight: '20px' }}>
              If you have any questions about this Privacy Policy or our data practices,
              please don&apos;t hesitate to contact us.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="mailto:privacy@secureonelabs.com"
                style={{ color: '#1e638d', fontSize: '16px' }}
                className="font-medium hover:underline transition-colors duration-200"
              >
                privacy@secureonelabs.com
              </a>

              <span className="hidden sm:block text-gray-400">|</span>

              <button
                style={{ color: '#1e638d', fontSize: '16px' }}
                className="font-medium hover:underline transition-colors duration-200"
                onClick={() => onNavigateToTop('/contact')}
              >
                Contact Support
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicyPage;
